import { DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
} from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  LanguageRow,
  ListResponse,
  ManagedSpeechRow,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

interface SpeechFormValues {
  schema: string;
  title: string;
  language_id: number;
}

/**
 * Sidebar entry "schema_view" → /speech/index.
 *
 * The talk catalogue: the numbered outlines every congregation draws from, one
 * row per schema and language. Only the webmaster maintains it, because a
 * schema number means the same thing everywhere.
 *
 * A talk is never deleted, only deactivated — the assignments that reference it
 * have to keep resolving. The old application stopped there and its activate
 * button stayed commented out, so a talk deactivated by mistake was gone. Here
 * "include deactivated" brings those rows back into the list, greyed out and
 * sorted last, where they can be activated again.
 */
export function SpeechManagementPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SpeechFormValues>();

  const [filters, setFilters] = useState<FilterValues>({});
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<ManagedSpeechRow | "new" | null>(null);

  const speeches = useQuery({
    queryKey: ["speeches", filters, includeInactive],
    queryFn: () =>
      apiFetch<ListResponse<ManagedSpeechRow>>(
        // Left out entirely when false — withQuery drops empty values, and the
        // server reads the parameter as a boolean.
        withQuery("/speeches", { ...filters, include_inactive: includeInactive ? "1" : null }),
      ),
  });

  // The editor needs the languages by id, and the same webmaster gate covers
  // both endpoints. Cached for the whole session: this list changes about as
  // often as a new language is rolled out.
  const languages = useQuery({
    queryKey: ["webmaster", "languages"],
    queryFn: () => apiFetch<ListResponse<LanguageRow>>("/webmaster/languages"),
    staleTime: Infinity,
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["speeches"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: (values: SpeechFormValues) =>
      editing === "new" || editing === null
        ? apiFetch<NotificationsResponse>("/speeches", { method: "POST", body: values })
        : apiFetch<NotificationsResponse>(`/speeches/${editing.id}`, {
            method: "PUT",
            body: values,
          }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/speeches/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const activate = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/speeches/${id}/activate`, { method: "PUT" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: ManagedSpeechRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row !== "new") {
        form.setFieldsValue({
          schema: row.schema ?? "",
          title: row.title ?? "",
          language_id: row.language_id ?? undefined,
        } as SpeechFormValues);
      }
    },
    [form],
  );

  const askAndDeactivate = useCallback(
    (row: ManagedSpeechRow) => {
      // The old grid removed a talk without asking. It does not come back
      // through this screen, so it asks.
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("dialog.confirm_deactivate", {
          name: [row.schema, row.title].filter(Boolean).join(" – "),
        }),
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: () => deactivate.mutate(row.id),
      });
    },
    [modal, t, deactivate],
  );

  const languageOptions = useMemo(
    () =>
      (languages.data?.data ?? []).map((language) => ({
        value: language.id,
        label: [language.language_code, language.denomination].filter(Boolean).join(" – "),
      })),
    [languages.data],
  );

  const fields = useMemo(
    () => [
      { name: "schema", label: t("activerecord.attributes.speech.schema") },
      { name: "title", label: t("activerecord.attributes.speech.title") },
      { name: "language_code", label: t("activerecord.attributes.speech.language_id") },
    ],
    [t],
  );

  const busy = save.isPending || deactivate.isPending || activate.isPending;

  const columns = useMemo<TableProps<ManagedSpeechRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        align: "right",
        defaultSortOrder: "ascend",
        // Schema numbers are stored as text; sorted as text, "10" lands before "9".
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
        sorter: (a, b) => compareText(a.title, b.title),
        render: (value: string | null, row) =>
          row.active ? (
            value
          ) : (
            <Space size={4}>
              {value}
              <Tag color="default">{t("speech.inactive")}</Tag>
            </Space>
          ),
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
      {
        key: "actions",
        fixed: "right",
        width: 100,
        render: (_value, row) => (
          <Space size={4}>
            <Tooltip title={t("form.update")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={() => openEditor(row)}
              />
            </Tooltip>
            {row.active ? (
              <Tooltip title={t("speech.disable")}>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={busy}
                  onClick={() => askAndDeactivate(row)}
                />
              </Tooltip>
            ) : (
              // Only reachable with "include deactivated" switched on — which is
              // the whole reason that switch exists.
              <Tooltip title={t("speech.activate")}>
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  disabled={busy}
                  onClick={() => activate.mutate(row.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, busy, openEditor, askAndDeactivate, activate],
  );

  return (
    <Card
      title={t("navigation.schema_view")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("speech.create")}
        </Button>
      }
    >
      <FilterBar fields={fields} onApply={setFilters} loading={speeches.isFetching} />

      <Checkbox
        checked={includeInactive}
        onChange={(event) => setIncludeInactive(event.target.checked)}
        style={{ marginBottom: 16 }}
      >
        {t("speech.show_inactive")}
      </Checkbox>

      <DataTable<ManagedSpeechRow>
        columns={columns}
        rows={speeches.data?.data}
        exportAs={t("navigation.schema_view")}
        loading={speeches.isPending || busy}
        error={speeches.error}
      />

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("speech.create") : t("activerecord.models.speech")}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<SpeechFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item
            name="schema"
            label={t("activerecord.attributes.speech.schema")}
            rules={[{ required: true }]}
          >
            <Input style={{ width: 140 }} />
          </Form.Item>

          <Form.Item
            name="title"
            label={t("activerecord.attributes.speech.title")}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="language_id"
            label={t("activerecord.attributes.speech.language_id")}
            // The server rejects a missing or inactive language with
            // "language.not_valid" — asking here saves the round trip.
            rules={[{ required: true }]}
          >
            <Select
              options={languageOptions}
              loading={languages.isPending}
              style={{ width: 260 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
